import { IChatMessage, resolveWelcomeActions } from 'ai-assistant/models/chat-message';
import { getRegisteredAssistantMentionLabels } from 'ai-assistant/utils/assistantChatCommands';
import { getMentionChipLabel, parseInputIntoParts } from 'ai-assistant/utils/mentionTokens';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState, type ComponentProps } from 'react';

import './AssistantMessage.light.scss';
import useAssistantChat, { AssistantChatStatus, ToolExecution, AssistantMessageBlock } from 'ai-assistant/hooks/useAssistantChat';
import {
  getCurrentModelTemplate,
  getTemplateInputLabel,
  getTemplateInputValueLabel,
} from 'components/Template/pathLabel';
import { Collapse, Icon, Intent } from '@blueprintjs/core';
import type { IconName } from '@blueprintjs/core';
import { AssistantMessageFeedback } from './assistant-message-feedback/AssistantMessageFeedback';

interface Props {
  message: IChatMessage;
  chat: ReturnType<typeof useAssistantChat>;
  isMostRecentMessage: boolean;
}

const TOOL_BASE_LABELS: Record<string, string> = {
  notify_model_input_changed: 'Changing input',
  notify_model_updated: 'Updating model',
  broadcast_custom_event: 'Sending update',
  send_ping: 'Checking connection',
  get_ess_data: 'Fetching ESS data',
  get_parameter_section: 'Loading parameter section',
  list_eset_tools: 'Listing ESET tools',
  get_eset_tool_details: 'Loading tool details',
  search_models: 'Searching models',
  get_eset_sample_case: 'Loading sample case',
};

const formatToolName = (toolName: string) => {
  const trimmed = toolName?.trim();
  if (!trimmed) {
    return 'Tool';
  }

  const withSpaces = trimmed
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!withSpaces) {
    return trimmed;
  }

  return withSpaces.replace(/\b\w/g, char => char.toUpperCase());
};

const parseToolArguments = (payload: unknown): Record<string, unknown> | null => {
  if (payload == null) {
    return null;
  }

  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }

  if (typeof payload === 'object' && !Array.isArray(payload)) {
    return payload as Record<string, unknown>;
  }

  return null;
};

const pickToolArg = (
  args: Record<string, unknown>,
  keys: string[],
): unknown => {
  for (const key of keys) {
    if (args[key] !== undefined && args[key] !== null && args[key] !== '') {
      return args[key];
    }
  }
  return undefined;
};

const formatToolDetailValue = (value: unknown, maxLength = 56): string | null => {
  if (value === undefined || value === null) {
    return null;
  }

  let text: string;
  if (typeof value === 'string') {
    text = value.trim();
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    text = String(value);
  } else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }

  if (!text) {
    return null;
  }

  if (text.length > maxLength) {
    return `${text.slice(0, maxLength - 1)}…`;
  }

  return text;
};

const joinToolDetails = (parts: Array<string | null | undefined>): string | null => {
  const filtered = parts.filter((part): part is string => Boolean(part && part.trim()));
  if (!filtered.length) {
    return null;
  }
  return filtered.join(', ');
};

const getToolDetail = (toolName: string, args: Record<string, unknown> | null): string | null => {
  if (!args) {
    return null;
  }

  switch (toolName) {
    case 'get_parameter_section': {
      const section = pickToolArg(args, ['parameter', 'section', 'section_name', 'name', 'label']);
      if (typeof section === 'string') {
        // Section args are already UI labels (e.g. "Battery Energy Storage System (BESS)").
        return formatToolDetailValue(section);
      }
      return formatToolDetailValue(section);
    }

    case 'notify_model_input_changed': {
      const pathValue = pickToolArg(args, ['input_path', 'inputPath', 'path']);
      const path = typeof pathValue === 'string' ? pathValue.trim() : '';
      const template = getCurrentModelTemplate();
      const pathLabel = path
        ? formatToolDetailValue(getTemplateInputLabel(template, path), 72)
        : null;
      const rawValue = pickToolArg(args, ['new_value', 'newValue', 'value']);
      const valueLabel = path
        ? getTemplateInputValueLabel(template, path, rawValue, 36)
        : formatToolDetailValue(rawValue, 36);

      if (pathLabel && valueLabel) {
        return `${pathLabel} → ${valueLabel}`;
      }
      return pathLabel ?? valueLabel;
    }

    case 'notify_model_updated':
      return formatToolDetailValue(pickToolArg(args, ['model_name', 'modelName', 'name']));

    case 'get_ess_data':
      return joinToolDetails([
        formatToolDetailValue(pickToolArg(args, ['battery_type', 'batteryType', 'technology'])),
        formatToolDetailValue(pickToolArg(args, ['database_year', 'databaseYear', 'year'])),
      ]);

    case 'get_eset_tool_details':
    case 'get_eset_sample_case':
      return formatToolDetailValue(pickToolArg(args, ['model_type', 'modelType', 'name']));

    case 'search_models':
      return formatToolDetailValue(pickToolArg(args, ['query', 'q', 'search']), 64);

    case 'broadcast_custom_event':
      return formatToolDetailValue(pickToolArg(args, ['event_type', 'eventType', 'type']));

    case 'send_ping':
      return formatToolDetailValue(pickToolArg(args, ['message']), 48);

    default: {
      // Generic fallback: surface identifying fields, preferring path labels when present.
      const pathValue = pickToolArg(args, ['input_path', 'inputPath', 'path']);
      if (typeof pathValue === 'string' && pathValue.trim()) {
        return formatToolDetailValue(
          getTemplateInputLabel(getCurrentModelTemplate(), pathValue),
          72,
        );
      }

      return joinToolDetails([
        formatToolDetailValue(pickToolArg(args, [
          'label',
          'title',
          'name',
          'parameter',
          'section',
          'query',
          'model_type',
          'modelType',
          'model_name',
          'modelName',
        ])),
      ]);
    }
  }
};

const getToolDisplayName = (toolName: string, argumentsPayload?: unknown) => {
  const base = TOOL_BASE_LABELS[toolName] ?? formatToolName(toolName);
  const detail = getToolDetail(toolName, parseToolArguments(argumentsPayload));

  if (detail) {
    return `${base}: ${detail}`;
  }

  // Without key details, keep the softer trailing ellipsis for in-progress feel.
  return TOOL_BASE_LABELS[toolName] ? `${base}...` : base;
};

const MarkdownLink = (props: ComponentProps<'a'>) => {
  const { children, ...rest } = props;
  return (
    <a {...rest} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

export function AssistantMessage({
  message,
  chat,
  isMostRecentMessage
}: Props) {
  const [expandedToolIds, setExpandedToolIds] = useState<Record<string, boolean>>({});
  const toolExecutions = useMemo(
    () => (message.role === 'assistant'
      ? chat.toolExecutionsByMessage?.[message.id] ?? []
      : []),
    [message.id, message.role, chat.toolExecutionsByMessage]
  );
  const assistantMessageBlocks = useMemo<AssistantMessageBlock[]>(() => {
    if (message.role !== 'assistant') {
      return [];
    }

    const blocks = chat.assistantMessageBlocksById?.[message.id];
    if (blocks && blocks.length) {
      return blocks;
    }

    if (!message.content) {
      return [];
    }

    return [{
      type: 'message',
      id: `${message.id}-content`,
      content: message.content,
    }];
  }, [chat.assistantMessageBlocksById, message.content, message.id, message.role]);

  useEffect(() => {
    setExpandedToolIds(prev => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach(id => {
        if (!toolExecutions.some(execution => execution.id === id)) {
          delete next[id];
          changed = true;
        }
      });

      // Keep new tool rows collapsed by default for a quieter presentation.
      toolExecutions.forEach(execution => {
        if (!(execution.id in next)) {
          next[execution.id] = false;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [toolExecutions]);

  const statusMeta: Record<ToolExecution['status'], { label: string; intent: Intent; icon: IconName }> = {
    started: { label: 'Running', intent: Intent.NONE, icon: 'refresh' },
    completed: { label: 'Done', intent: Intent.NONE, icon: 'tick' },
    failed: { label: 'Failed', intent: Intent.DANGER, icon: 'error' },
    pending_approval: { label: 'Needs review', intent: Intent.WARNING, icon: 'time' },
    rejected: { label: 'Rejected', intent: Intent.DANGER, icon: 'cross' },
  };

  const renderUserMessageContent = () => {
    const parts = parseInputIntoParts(message.content);
    const hasMentions = parts.some(part => part.type === 'mention');

    if (!hasMentions) {
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: MarkdownLink,
          }}
        >
          {message.content}
        </ReactMarkdown>
      );
    }

    const labelByPath = getRegisteredAssistantMentionLabels();

    return (
      <span className="assistant-message__mention-body">
        {parts.map((part, index) => {
          if (part.type !== 'mention') {
            return (
              <span key={`text-${index}`} className="assistant-message__mention-text">
                {part.value}
              </span>
            );
          }

          const chipLabel = getMentionChipLabel(part.value, labelByPath);
          return (
            <span
              key={`mention-${index}-${part.value}`}
              className="assistant-message__mention-chip"
              title={part.value}
            >
              <span className="assistant-message__mention-chip-icon" aria-hidden="true">@</span>
              <span className="assistant-message__mention-chip-label">{chipLabel}</span>
            </span>
          );
        })}
      </span>
    );
  };

  const hasRenderableAssistantContent = assistantMessageBlocks.some(block => {
    if (block.type === 'message') {
      return Boolean(block.content?.trim());
    }

    return toolExecutions.some(execution => execution.id === block.id);
  });

  const shouldShowLoading =
    message.role === 'assistant'
    && isMostRecentMessage
    && (chat.status === AssistantChatStatus.SUBMITTED || chat.status === AssistantChatStatus.STREAMING)
    && !hasRenderableAssistantContent;

  const welcomeActions = useMemo(() => {
    if (!isMostRecentMessage || message.role !== 'assistant' || !message.isWelcome) {
      return [];
    }

    return resolveWelcomeActions({
      isModelPage: chat.isModelPage,
      hasModelOutput: chat.hasModelOutput,
    });
  }, [chat.hasModelOutput, chat.isModelPage, isMostRecentMessage, message.isWelcome, message.role]);

  if (shouldShowLoading) {
    return (
      <p className='assistant-message__loading-text assistant-message__loading-text-base'>One moment please...</p>
    )
  }

  const formatPayloadPreview = (payload: unknown): string => {
    if (payload === undefined || payload === null) {
      return '';
    }

    try {
      const serialized = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
      if (serialized.length > 280) {
        return `${serialized.slice(0, 277)}...`;
      }
      return serialized;
    } catch (error) {
      console.error('AssistantMessage: failed to format tool payload', error);
      return String(payload);
    }
  };

  const renderToolExecutions = (executions: ToolExecution[]) => {
    if (!executions.length) {
      return null;
    }

    const toggleToolExecution = (id: string) => {
      setExpandedToolIds(prev => ({
        ...prev,
        [id]: !prev[id],
      }));
    };

    return (
      <div className='assistant-message__tool-events'>
        <div className='assistant-message__tool-events__list'>
          {executions.map(execution => {
            const status = statusMeta[execution.status];
            const isExpanded = Boolean(expandedToolIds[execution.id]);
            const toolDisplayName = getToolDisplayName(execution.toolName, execution.arguments);
            const hasDetails = (
              execution.arguments !== undefined
              || Boolean(execution.responsePreview && execution.status === 'completed')
              || Boolean(execution.error)
            );
            const showStatusLabel = execution.status !== 'completed' && execution.status !== 'started';
            const rowClassName = [
              'assistant-message__tool-events__row',
              hasDetails ? 'assistant-message__tool-events__row--expandable' : '',
              isExpanded ? 'assistant-message__tool-events__row--expanded' : '',
              execution.status === 'started' ? 'assistant-message__tool-events__row--running' : '',
              execution.status === 'failed' || execution.status === 'rejected'
                ? 'assistant-message__tool-events__row--error'
                : '',
            ].filter(Boolean).join(' ');

            return (
              <div key={execution.id} className={rowClassName}>
                <div
                  className='assistant-message__tool-events__summary'
                  role={hasDetails ? 'button' : undefined}
                  tabIndex={hasDetails ? 0 : undefined}
                  aria-expanded={hasDetails ? isExpanded : undefined}
                  onClick={hasDetails ? () => toggleToolExecution(execution.id) : undefined}
                  onKeyDown={hasDetails ? (event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleToolExecution(execution.id);
                    }
                  }) : undefined}
                >
                  <Icon
                    icon={status.icon}
                    size={12}
                    className='assistant-message__tool-events__status-icon'
                    intent={status.intent}
                  />
                  <span className='assistant-message__tool-events__label'>{toolDisplayName}</span>
                  {showStatusLabel && (
                    <span className='assistant-message__tool-events__status-label'>{status.label}</span>
                  )}
                  {hasDetails && (
                    <Icon
                      icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                      size={12}
                      className='assistant-message__tool-events__chevron'
                    />
                  )}
                </div>

                {hasDetails && (
                  <Collapse isOpen={isExpanded}>
                    <div className='assistant-message__tool-events__details'>
                      {execution.arguments !== undefined && (
                        <div className='assistant-message__tool-events__section'>
                          <span className='assistant-message__tool-events__section-label'>Input</span>
                          <pre className='assistant-message__tool-events__code'>
                            {formatPayloadPreview(execution.arguments)}
                          </pre>
                        </div>
                      )}

                      {execution.responsePreview && execution.status === 'completed' && (
                        <div className='assistant-message__tool-events__section'>
                          <span className='assistant-message__tool-events__section-label'>Output</span>
                          <pre className='assistant-message__tool-events__code'>
                            {formatPayloadPreview(execution.responsePreview)}
                          </pre>
                        </div>
                      )}

                      {execution.error && (
                        <div className='assistant-message__tool-events__section'>
                          <span className='assistant-message__tool-events__section-label'>Error</span>
                          <pre className='assistant-message__tool-events__code assistant-message__tool-events__code--error'>
                            {formatPayloadPreview(execution.error)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </Collapse>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAssistantBlocks = () => {
    if (!assistantMessageBlocks.length) {
      return null;
    }

    return (
      <div className='assistant-message__stack'>
        {assistantMessageBlocks.map(block => {
          if (block.type === 'message') {
            return (
              <div key={block.id} className='assistant-message__content'>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: MarkdownLink,
                  }}
                >
                  {block.content}
                </ReactMarkdown>
              </div>
            );
          }

          const execution = toolExecutions.find(item => item.id === block.id);
          if (!execution) {
            return null;
          }

          return (
            <div key={`tool-${block.id}`} className='assistant-message__tool-block'>
              {renderToolExecutions([execution])}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`assistant-message assistant-message--${message.role}`}>
      {message.role === 'user' ? (
        <motion.div
          layoutId={chat.coordinator.stagedFlowMessageIndex !== null
            ? `flow-message-${chat.coordinator.stagedFlowMessageIndex}`
            : isMostRecentMessage
              ? `message-container-${message.id}`
              : undefined}
          className='assistant-message__content'
          style={{
            borderRadius: '12px 12px 0px 12px',
          }}
          transition={{
            duration: 0.5,
            ease: 'easeOut'
          }}
          onLayoutAnimationComplete={chat.coordinator.stagedFlowMessageIndex !== null ? chat.coordinator.handleFlowMessageAnimationFinish : chat.coordinator.handleUserMessageAnimationFinish}
        >
          {renderUserMessageContent()}
        </motion.div>
      ) : (
        <>
          {message.role === 'assistant' ? (
            renderAssistantBlocks()
          ) : (
            <div className='assistant-message__content'>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: MarkdownLink,
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {message.role !== 'system' && (
            <AssistantMessageFeedback chat={chat} messageId={message.id} />
          )}
        </>
      )}

      {welcomeActions.length > 0 && (
        <div className='assistant-message__flow'>
          {welcomeActions.map((action, index) => {
            const isDisabled =
              chat.status === AssistantChatStatus.SUBMITTED
              || chat.status === AssistantChatStatus.STREAMING
              || chat.hasPendingToolApprovals;

            return (
              <motion.div
                key={action.id}
                layoutId={action.kind === 'canned' ? `flow-message-${index}` : undefined}
                className='assistant-message__flow-option'
                role='button'
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) {
                    void chat.handleWelcomeAction(action, index);
                  }
                }}
                onKeyDown={event => {
                  if (isDisabled) {
                    return;
                  }
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    void chat.handleWelcomeAction(action, index);
                  }
                }}
                whileHover={isDisabled ? undefined : { scale: 1.02 }}
                style={{ borderRadius: '12px' }}
                transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }}
              >
                <ReactMarkdown
                  className='assistant-message__flow-option-text'
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: MarkdownLink,
                    p: ({ node: _node, ...props }) => <p {...props} />,
                  }}
                >
                  {action.kind === 'prompt' ? action.prompt : action.label}
                </ReactMarkdown>
                <Icon icon="send-message" />
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
